'use client';

import { editUser } from '@/apis/ApiUtil';
import { useUserLogged } from '@/hooks/useUserLogged';
import useOrganizationStore from '@/state/orgState';
import User from '@/types/userType';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { useForm } from 'react-hook-form';
import { Copy, Link2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDeleteUser } from '@/hooks/useTeamData';
import { getFrontUrl } from '@/lib/auth/frontUrl';
import { isAdminOrOwner, isOwner } from '@/lib/rbac';
import { useQuery } from '@tanstack/react-query';
import { getOrCreateInviteToken } from '@/apis/inviteTokens';
interface EditUserModalProps {
  editIsOpen: boolean;
  userBeingEdited: User;
  setUserBeingEdited: (user: User | null) => void;
  editOnClose: () => void;
  orgArr: User[];
  setOrgArr: (orgArr: User[]) => void;
  setTeamsWithMembers: (teamsWithMembers: TeamTypeWithMembers[]) => void;
  teamsWithMembers: TeamTypeWithMembers[];
  /**
   * Org-wide count of admins, for callers whose `orgArr` is one page of a
   * paginated roster. Counting admins in a page would let the last admin be
   * removed whenever they happen to be the only admin on the loaded page.
   * Callers holding the full roster can leave this undefined.
   */
  adminCount?: number;
}
const frontURL = getFrontUrl();

export const EditUserModal = ({
  editIsOpen,
  orgArr,
  setOrgArr,
  teamsWithMembers,
  setTeamsWithMembers,
  userBeingEdited,
  setUserBeingEdited,
  editOnClose,
  adminCount,
}: EditUserModalProps) => {
  const { user } = useUserLogged();
  const [activeUser, setActiveUser] = useState(true);
  const organization = useOrganizationStore((state) => state.organization);
  const { register, handleSubmit, reset } = useForm();
  const toast = useToast();
  const orgUuid = organization?.OrgUUID ?? '';
  const userUuid = user?.UserUUID ?? '';
  const { data: tokenRes } = useQuery({
    queryKey: ['inviteToken', orgUuid, userUuid],
    queryFn: () =>
      getOrCreateInviteToken(
        orgUuid,
        userUuid,
        '48h',
        false,
        'member',
        true,
        // An Owner's own team: the same default link the invite card shows.
        // Admins send none; the backend always uses their own team.
        isOwner(user?.Role) ? user?.TeamUUID || undefined : undefined
      ),
    // Gated on the modal being open. This modal is rendered closed alongside
    // the members table, so without editIsOpen it minted an invite token on
    // every page mount: a POST nobody asked for, for a link nobody can see.
    // AddMemberModal already gates its equivalent query this way.
    enabled: editIsOpen && !!orgUuid && !!userUuid,
  });
  const inviteLink = tokenRes?.Token
    ? `${frontURL}/invite/${tokenRes.Token}`
    : '';

  // Hook for deleting users
  const deleteUserMutation = useDeleteUser(
    organization?.OrgUUID,
    user?.accessToken
  );

  const handleCopyLink = (inviteLink: string) => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    toast({
      title: 'Link copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  useEffect(() => {
    if (!userBeingEdited?.Email) {
      setActiveUser(false);
    } else {
      setActiveUser(true);
    }
  }, [userBeingEdited]);

  const handleEditSubmit = async (formData: any) => {
    if (userBeingEdited) {
      try {
        await editUser(
          userBeingEdited.UserUUID,
          formData.userName.split(' ')[0],
          formData.userName.split(' ')[1],
          userBeingEdited.Role // Keep the existing role
        );

        const modifiedUser = {
          ...userBeingEdited,
          FirstName: formData.userName.split(' ')[0],
          LastName: formData.userName.split(' ')[1],
          // Role remains unchanged
        };

        setOrgArr(
          orgArr.map((orgUser: User) =>
            orgUser.UserUUID === userBeingEdited.UserUUID
              ? { ...orgUser, ...modifiedUser }
              : orgUser
          )
        );

        setUserBeingEdited(null);
        reset();
        editOnClose();
      } catch (error) {
        console.error('Update failed:', error);
      }
    }
  };

  const isOnlyAdmin = (currentUserUUID: string): boolean => {
    const admins = orgArr.filter((user) => isAdminOrOwner(user.Role));
    // adminCount is authoritative when the caller passes it: its orgArr is only
    // one page, so a page-local count of 1 says nothing about the org.
    const total = adminCount ?? admins.length;
    if (total !== 1) return false;
    return admins.some(
      (admin) =>
        admin.UserUUID === currentUserUUID && isAdminOrOwner(admin.Role)
    );
  };
  const handleRemoveMember = async () => {
    if (!userBeingEdited?.UserUUID) return;

    try {
      // The member's own org, not the viewer's -- see useDeleteUser.
      await deleteUserMutation.mutateAsync({
        userUUID: userBeingEdited.UserUUID,
        organizationUUID: userBeingEdited.OrganizationUUID,
      });

      // Update local state
      setOrgArr(
        orgArr.filter((user) => user.UserUUID !== userBeingEdited?.UserUUID)
      );
      const updatedTeamsWithMembers = [...teamsWithMembers];
      updatedTeamsWithMembers.forEach((team) => {
        const memberIndex = team.TeamMembers.findIndex(
          (t) =>
            t.login === userBeingEdited?.GithubUsername ||
            t.login === userBeingEdited?.GitlabUsername
        );
        if (memberIndex !== -1) {
          team.TeamMembers.splice(memberIndex, 1);
        }
      });
      setTeamsWithMembers(updatedTeamsWithMembers);
      setUserBeingEdited(null);
      reset();
      editOnClose();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setUserBeingEdited(null);
      reset();
      editOnClose();
    }
  };

  return (
    <Dialog open={editIsOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-ds-surface-white text-ds-text-darker border-ds-border-subtlest">
        <DialogHeader>
          <DialogTitle>
            {activeUser
              ? 'Edit User'
              : `Invite ${userBeingEdited?.GithubUsername ?? userBeingEdited?.GitlabUsername}`}
          </DialogTitle>
        </DialogHeader>

        {activeUser ? (
          <form onSubmit={handleSubmit(handleEditSubmit)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-sm text-ds-text-subtle">Name</Label>
                <Input
                  {...register('userName')}
                  // No login fallback: whatever sits in this box is saved
                  // as the person's name.
                  defaultValue={[
                    userBeingEdited?.FirstName,
                    userBeingEdited?.LastName,
                  ]
                    .filter(
                      (part) => part && part !== 'null' && part !== 'undefined'
                    )
                    .join(' ')}
                  placeholder="User Name"
                  className="bg-ds-surface-white border-ds-border-subtlest text-ds-text-darker placeholder:text-ds-text-subtle"
                />
              </div>
            </div>
            <DialogFooter className="flex justify-end gap-2">
              <Button type="submit" className="font-medium">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="text-ds-brand-subtler size-5 shrink-0" />
                <span className="text-xs">Share the link to invite</span>
              </div>
              <p className="text-ds-text-subtle mb-4 text-xs">
                Copy the link below and share, expires in 24 hours.
              </p>
              <div className="bg-primitive-gray-100 p-3 mb-4 text-ds-text-default rounded-md">
                {inviteLink}{' '}
                <Copy
                  className="text-ds-brand-subtler cursor-pointer inline-block align-middle"
                  size={16}
                  onClick={() => handleCopyLink(inviteLink)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="destructive"
                className="font-medium"
                onClick={handleRemoveMember}
              >
                Remove{' '}
                {userBeingEdited?.GithubUsername ??
                  userBeingEdited?.GitlabUsername}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
